import { RideStatusBadge } from './RideStatusBadge';
import { getCancellationReason, getContractRef, getExternalRef, getPickupAtISO, getRideStatus } from './trip';
import { formatAbsoluteDateTime, formatShortDateTime } from '../../lib/formatters';
import type { ExtranetTripOutput } from '../../types/extranet-api.types';
import { IconInbox } from '../incidents/icons';

interface RidesTableProps {
  items: ExtranetTripOutput[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  onRowAction?: (trip: ExtranetTripOutput) => void;
  /** Si défini, montre un menu d'action (ex. "Signaler une réclamation") */
  actionLabel?: string;
}

const SKELETON_ROWS = 10;

export const RidesTable = ({ items, total, isLoading, isFetching, onRowAction, actionLabel }: RidesTableProps) => (
  <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50/40">
            <Th>Réf. Allocab</Th>
            <Th>GALAC&apos;SI</Th>
            <Th>Contrat</Th>
            <Th>BUPO</Th>
            <Th>Horaire PEC</Th>
            <Th>Adresse PEC</Th>
            <Th>Adresse dépose</Th>
            <Th>Statut</Th>
            {onRowAction ? <Th align="right">Actions</Th> : null}
          </tr>
        </thead>
        <tbody className={isFetching && !isLoading ? 'opacity-60 transition-opacity' : ''}>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <SkeletonRow key={i} hasAction={Boolean(onRowAction)} />
            ))
          ) : items.length === 0 ? (
            <EmptyRow colSpan={onRowAction ? 9 : 8} />
          ) : (
            items.map(trip => {
              const pickupAt = getPickupAtISO(trip);
              const pickupAddress = trip.pickupAddress;
              const dropoffAddress = trip.dropoffAddress;
              return (
                <tr
                  key={trip.id}
                  className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/60 transition-colors duration-100"
                >
                  <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-900 whitespace-nowrap">
                    {trip.id.slice(-12)}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap">
                    {getExternalRef(trip)}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap">
                    {getContractRef(trip)}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap">
                    {/* Le back livre siteRef (BUPO-...) mais pas le libellé siteName. À enrichir. */}
                    {trip.siteRef || '—'}
                  </td>
                  <td
                    className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-700 whitespace-nowrap"
                    title={formatAbsoluteDateTime(pickupAt)}
                  >
                    {formatShortDateTime(pickupAt)}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 max-w-[200px]">
                    <span className="block truncate" title={pickupAddress}>
                      {pickupAddress}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 max-w-[200px]">
                    <span className="block truncate" title={dropoffAddress}>
                      {dropoffAddress}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <RideStatusBadge status={getRideStatus(trip)} cancellationReason={getCancellationReason(trip)} />
                  </td>
                  {onRowAction ? (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => onRowAction(trip)}
                        className="text-xs text-zinc-500 hover:text-zinc-900 underline-offset-2 hover:underline transition-colors"
                      >
                        {actionLabel ?? 'Action'}
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
    {!isLoading && items.length > 0 ? (
      <div className="px-4 py-2.5 text-xs text-zinc-500 border-t border-zinc-100 flex items-center justify-between">
        <span>
          <span className="font-mono tabular-nums text-zinc-700">{items.length}</span> affichée
          {items.length > 1 ? 's' : ''} sur <span className="font-mono tabular-nums text-zinc-700">{total}</span>
        </span>
      </div>
    ) : null}
  </div>
);

const Th = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <th
    className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-zinc-500 ${align === 'right' ? 'text-right' : 'text-left'}`}
  >
    {children}
  </th>
);

const SkeletonRow = ({ hasAction }: { hasAction: boolean }) => (
  <tr className="border-b border-zinc-100 last:border-b-0">
    {Array.from({ length: hasAction ? 9 : 8 }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3 rounded-sm skeleton-pulse"
          style={{ width: `${[70, 60, 65, 55, 50, 80, 80, 45, 30][i] ?? 60}%` }}
        />
      </td>
    ))}
  </tr>
);

const EmptyRow = ({ colSpan }: { colSpan: number }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-16">
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <IconInbox width={28} height={28} className="text-zinc-300" />
        <div>
          <p className="text-sm text-zinc-900">Aucune course sur ce périmètre.</p>
          <p className="text-xs text-zinc-500 mt-1">
            Ajustez les filtres ou élargissez la période pour afficher davantage de résultats.
          </p>
        </div>
      </div>
    </td>
  </tr>
);
